import { useQuery } from '@tanstack/react-query';

import { queryOptions } from '~components/hooks/_constants';
import { useContractVaultManager } from '~components/hooks/contracts';
import { useGraphStatus } from '~components/hooks/useGraphStatus';
import { checkValidContractAddress, getContractAddress } from '~config/protocol';
import { ENativeAsset, EVestaAsset } from '~config/types';
import { queryClient } from '~pages/_app';
import { getChainSystemVault, getGraphSystemVaults } from '~services/contracts/troves';
import { GraphSystemVault } from '~services/subgraph/vesta/types';
import { BIG_INT_ZERO, determineAssetPriceUSD } from '~utils/utils';

import { useVestaSubgraph } from '../../graph/useVestaSubgraph';
import { useSession } from '../../useSession';
import { ComputedVaultData, DeterminedQueryResult, VaultQuery } from '../_types';
import useAssetPrices from '../assets/useAssetPrices';

export const initialVaultData: GraphSystemVault = {
  currentAssetSystemState: {
    totalCollateral: '0',
    totalCollateralRaw: BIG_INT_ZERO,
    totalDebt: '0',
    totalDebtRaw: BIG_INT_ZERO,
    totalCollateralRatio: '0',
    unpaidInterest: '0',
  },
};

export const initialComputedVaultData: ComputedVaultData = {
  totalCollateralUsd: 0,
  totalDebtUsd: 0,
  collateralRatio: 0,
};

export const VaultDataQueryKey = 'vault-data';

export const useVaultData = (asset: EVestaAsset): DeterminedQueryResult<VaultQuery> => {
  const {
    network: { networkId },
  } = useSession();
  const { data: prices, isLoading: isPricesLoading } = useAssetPrices();
  const { data: isGraphFetchable } = useGraphStatus();
  const vestaSubgraph = useVestaSubgraph();
  const vaultManager = useContractVaultManager();
  const queryKey = [VaultDataQueryKey, networkId, asset, isGraphFetchable ? 'graph' : 'chain'];
  const invalidate = () => queryClient.refetchQueries(queryKey);
  const assetAddress = getContractAddress(asset, networkId);

  const result = useQuery(
    queryKey,
    async () => {
      try {
        return isGraphFetchable
          ? (await getGraphSystemVaults(vestaSubgraph)).vaultInfo[asset]
          : (
              await getChainSystemVault({
                assetAddress,
                collatPrice: prices.oracle[asset],
                vaultManagerRead: vaultManager.read,
              })
            ).vaultInfo;
      } catch (err) {
        console.error(err);
      }
    },
    queryOptions(false, !isPricesLoading && checkValidContractAddress([vaultManager.address])),
  );

  const vaultData = result.data ?? initialVaultData;

  return {
    ...result,
    data: {
      vault: vaultData,
      computed: {
        totalCollateralUsd: determineAssetPriceUSD(
          parseFloat(vaultData.currentAssetSystemState.totalCollateral),
          prices.oracle[asset],
        ),
        totalDebtUsd:
          parseFloat(vaultData.currentAssetSystemState.totalDebt) * prices.market[ENativeAsset.VST],
        collateralRatio: parseFloat(vaultData.currentAssetSystemState.totalCollateralRatio),
      },
    },
    invalidate,
  } as DeterminedQueryResult<VaultQuery>;
};

export default useVaultData;
